//
import { getDefaultState } from './state';
import { defineStore } from 'pinia';
import * as getters from './getters';
import {
  type IDebtSolutionsStore,
  type IDebtSolutionsStorePlanWOStacking,
  type IDebtSolutionsStorePlanWStacking,
  type IDebtSolutionsStoreDebt,
  type IPersonInfo,
  type ICustomerStoreDebtSolutions,
  type IDebtSolutionsStoreScenario,
} from './debtsolutions.model';
import bff from '@/shared/api/bff';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import type {
  ICustomerStoreInvestments,
  ICustomerStoreInvestmentsGroup,
  ICustomerStoreInvestmentsGroupAccount,
} from '@/stores/customer/customerStore.model.ts';
import type { IPhase } from '@/modules/policy/models/Phase.ts';
import { differenceInYears, format, formatISO } from 'date-fns';

export const useDebtSolutionsStore = defineStore('debtsolutions', {
  state: getDefaultState,
  getters,
  actions: {
    setDebt(debt: IDebtSolutionsStoreDebt) {
      const foundIndex = this.debts.findIndex((item) => item.debtID === debt.debtID);
      if (foundIndex !== -1) {
        this.debts[foundIndex] = { ...this.debts[foundIndex], ...debt };
      } else {
        this.debts.push(debt);
      }
    },
    deleteDebt(debt: IDebtSolutionsStoreDebt) {
      const foundIndex = this.debts.findIndex((item) => item.debtID === debt.debtID);
      if (foundIndex !== -1) {
        this.debts.splice(foundIndex, 1);
      }
    },

    setPlanWOStacking(plan: IDebtSolutionsStorePlanWOStacking) {
      this.planWOStacking = plan;
    },
    setPlanWStacking(plan: IDebtSolutionsStorePlanWStacking) {
      this.planWStacking = plan;
    },

    setAddADebtOpenFlag(isOpen: boolean) {
      this.addADebtOpen = isOpen;
    },

    resetState(state: IDebtSolutionsStore) {
      Object.assign(state, getDefaultState());
    },

    setScenario(scenario: IDebtSolutionsStoreScenario) {
      this.scenario = scenario;
    },

    async pullDebts() {
      const customerStore = useCustomerStore();

      // {"scenarios":[{"agentId":"","state":"","country":"","includeSpouse":0,"retirementROR":0.08,"debtInfo":[],"personInfo":[]}]}
      const response = await bff.getDebt();
      // throw error in case of non-200 response
      if (Math.floor(response.status / 100) !== 2) {
        // throw new Error('Pulling Debts failed.');
        return false;
      }

      const scenario = response.data.scenarios[0];
      this.scenario = scenario;
      if (scenario?.debtInfo) {
        const debt = scenario.debtInfo;
        debt.scenarioId = scenario.scenarioId;

        // cleaning up the scenario.debtname to only allow a-zA-Z0-9 and space
        scenario.debtInfo.forEach((debtObj1: { debtName: string }) => {
          debtObj1.debtName = debtObj1.debtName.replace(/[^A-Za-z0-9 ]/g, '');
        });
        this.debts = debt;
      }
      let personInfo = response.data.scenarios[0].personInfo[0];
      if (personInfo === undefined || personInfo === null) {
        personInfo = {
          personType: 'P',
          name: 'Primary Debt',
          currentAge: 0, // 46 Grab from  life or investment
          retirementAge: 0, // 67 Grab from  investment  else use default CA: 65 US: 67
          dob: '', // '1974-06-06T00:00:00' Grab from life or investment
          grossMonthlyIncome: 5000.0,
        };
      }
      if (personInfo.dob.includes(' ')) {
        // Changed format from "Wed Dec 31 1969" to "1969-12-31T00:00:00-00:00"
        personInfo.dob = formatISO(new Date(personInfo.dob));
      }

      const investments = customerStore.investments;
      const investScenario = this.getInvestment(investments);
      // Data coming in from Investment side
      if (investScenario != null && investScenario?.retirement?.age > 0) {
        personInfo.retirementAge = investScenario?.retirement?.age;
      }
      if (personInfo?.currentAge) {
        this.personInfo = personInfo;
      }
      return true;
    },
    getInvestment(investments: ICustomerStoreInvestments) {
      if (investments.calculator?.scenario) {
        return JSON.parse(JSON.stringify(investments.calculator.scenario));
      }
      const accounts: ICustomerStoreInvestmentsGroupAccount[] = [];
      investments.groups?.forEach((group: ICustomerStoreInvestmentsGroup) => {
        group.accounts?.forEach((account: ICustomerStoreInvestmentsGroupAccount) => {
          accounts.push({
            productType: account.productType,
            number: account.accountNumber,
            enabled: true,
            type: account.accountType,
            balance: account.endingValue,
          } as unknown as ICustomerStoreInvestmentsGroupAccount);
        });
      });
      return {
        currentIncome: 0,
        incomeGoal: 0,
        incomeGoalPct: 0,
        otherSavings: 0,
        monthlySavings: 0,
        scenarioId: -1,
        retirement: { accounts, savings: 0, age: 0 },
      };
    },
    async pushDebts(persondob: string | null | boolean) {
      const customerStore = useCustomerStore();
      const policyStore = usePolicyStore();
      // let lifeIssueDate = ''; // 06/28/2014
      // const investIssueDate = ''; // Currently can't find the date that the dob was registered or investments created

      let scenario = JSON.parse(JSON.stringify(this.scenario));

      // Check for
      if (scenario?.personInfo == null || scenario.personInfo.length === 0) {
        scenario = {
          scenarioId: -1,
          agentId: '', // 00028', This is not being looked at in Back End
          state: '', // 'GA
          country: '', // Need to do this 'US', 'CA'
          includeSpouse: 0,
          retirementROR: 0.08, // Need to do this Investment Guidance Calc
          personInfo: [
            {
              personType: 'P',
              name: 'Primary Debt',
              currentAge: 0, // 46 Grab from  life or investment
              retirementAge: 0, // 67 Grab from  investment  else use default CA: 65 US: 67
              dob: '', // '1974-06-06T00:00:00' Grab from life or investment
              grossMonthlyIncome: 5000.0,
            } as unknown as IPersonInfo | undefined,
          ] as unknown as IPersonInfo[] | undefined | null,
          debtInfo: null,
        };
      }
      // This is to grab the user updated DOB from MyDebt
      let tempPersonDob = '';
      if (persondob) {
        const tpersondob = new Date(persondob as string).toDateString();
        tempPersonDob = new Date(tpersondob).toDateString();
      } else if (typeof this.personInfo !== 'undefined' && this.personInfo?.dob != null) {
        tempPersonDob = JSON.parse(JSON.stringify(this.personInfo.dob));
      }
      // If not null, empty string for tempPersonDob and scenario.personInfo[0].dob is empty String
      if (
        typeof tempPersonDob !== 'undefined' &&
        tempPersonDob !== '' &&
        (scenario.personInfo[0]?.dob == null || scenario.personInfo[0]?.dob === '')
      ) {
        scenario.personInfo[0].dob = tempPersonDob;
        const tDobDate = new Date(tempPersonDob);
        // Only set the dob && currentAge if dob is null or empty string
        if (tDobDate instanceof Date && !isNaN(tDobDate.getTime())) {
          scenario.personInfo[0].currentAge = differenceInYears(new Date(), tDobDate);
        }
      }

      if (scenario.personInfo[0].dob === '' || scenario.personInfo[0].retirementAge === 0) {
        // Start of setting DOB from investment and life
        let investments;
        let investScenario;
        let currentPolicy;
        // Start a while loop that puts data from investment and life, keep running
        // until necessary data is set or for maxIteration.
        const maxIteration = 10; // 5 secs max
        // const iTimeOutInMilliseconds = 500;
        let iCount = 0;
        while (
          iCount < maxIteration &&
          (scenario.personInfo[0].dob === '' || scenario.personInfo[0].retirementAge === 0)
        ) {
          investments = customerStore.investments;

          investScenario = this.getInvestment(investments);
          // Data coming in from Investment side
          if (investScenario != null) {
            scenario.personInfo[0].retirementAge = investScenario?.retirement?.age;
            const tDate1 = new Date(investScenario?.dateOfBirth);
            // Only set the dob && currentAge if dob is null or empty string
            if (
              (scenario.personInfo[0].dob == null || scenario.personInfo[0].dob === '') &&
              tDate1 instanceof Date &&
              !isNaN(tDate1.getTime())
            ) {
              // Date needs to be in this format '1974-06-06T00:00:00'
              scenario.personInfo[0].dob = format(tDate1, 'YYYY-MM-DDTHH:mm:ss-00:00');
              scenario.personInfo[0].currentAge = differenceInYears(
                new Date(),
                scenario?.personInfo[0].dob
              );
            }
          }
          // Life Policy
          currentPolicy = policyStore.currentPolicy;

          // Setting agentId
          if (currentPolicy?.agentId != null) {
            scenario.agentId = currentPolicy?.agentId;
          }
          // Looking at life policy to fill the dob if not empty
          if (currentPolicy?.phases != null) {
            const phases = currentPolicy?.phases;
            phases.forEach((phase: IPhase | null) => {
              if (phase?.phaseNo === '01') {
                // Only set the dob && currentAge if dob is null or empty string
                if (scenario.personInfo[0].dob == null || scenario.personInfo[0].dob === '') {
                  const tDate2 = new Date(phase.dateOfBirth ?? '');
                  if (tDate2 instanceof Date && !isNaN(tDate2.getTime())) {
                    // Date needs to be in this format '1974-06-06T00:00:00'
                    scenario.personInfo[0].dob = format(tDate2, 'yyyy-MM-ddTHH:mm:ss-00:00');
                    scenario.personInfo[0].currentAge = differenceInYears(
                      new Date(),
                      scenario?.personInfo[0].dob
                    );
                  }
                }
                scenario.state = phase.issueState; // scenario.state = phase?.phIssueState;
                // This is the issue date for life
                // lifeIssueDate = moment(phase?.phIssueDate, 'MM/DD/YYYY');
              }
            });
          }
          iCount++;
          // await timeout(iTimeOutInMilliseconds);

          // await timeout(iTimeOutInMilliseconds);
        }
        // End a while loop

        // Catching any missing data and putting default
        if (scenario.agentId === '') {
          scenario.agentId = '00028';
        }

        // This is to put the country and state from customerProfile
        const customerProfileObj: ICustomerStoreDebtSolutions = customerStore.customerProfile;
        if (customerProfileObj.country.valueOf() === 'US') {
          scenario.country = 'US';
          if (scenario.state === '') {
            scenario.state = 'GA';
          }
        } else {
          scenario.country = 'CA';
          if (scenario.state === '') {
            scenario.state = 'ON';
          }
        }
        // This is to handle cases where for some reason no data in country and state
        if (scenario.country === '') {
          scenario.country = 'US';
        }
        if (scenario.state === '') {
          scenario.state = 'GA';
        }

        if (scenario.personInfo[0].retirementAge === 0) {
          scenario.personInfo[0].retirementAge = 67;
        }
        if (scenario.personInfo[0].dob === '') {
          scenario.personInfo[0].dob = '1974-06-06T00:00:00';
        }

        if (scenario.personInfo[0].currentAge === 0) {
          const tCurrentAge = differenceInYears(new Date(), scenario.personInfo[0].dob);
          scenario.personInfo[0].currentAge = tCurrentAge;
        }
        // This is to save the mocked up scenario obj
        // this.scenario = scenario;
        // End of setting DOB from investment and life
      }

      // Setting the ROR depending on the age till retirement, if more than 5 years 8% (default value),  else 6%
      const timeTillRetirement =
        scenario.personInfo[0].retirementAge -
        differenceInYears(new Date(), scenario?.personInfo[0].dob);
      if (timeTillRetirement < 5) {
        scenario.retirementROR = 0.06;
      } else {
        scenario.retirementROR = 0.08;
      }

      // Saving personInfo
      this.personInfo = scenario.personInfo[0];

      scenario.debtInfo = this.debts;
      if (this.scenario.scenarioId) {
        scenario.scenarioId = this.scenario.scenarioId;
      } else {
        scenario.scenarioId = -1;
      }

      // Saving scenario
      // this.setScenario(scenario);
      const response = await bff.postDebtAndCalculate(scenario);
      // throw error in case of non-200 response
      if (Math.floor(response.status / 100) !== 2) {
        // throw new Error('Pushing Debts failed.');
        return false;
      }
      // const plans = response.data.plans.planCollection;
      /*
      commit('PLAN_WO_STACKING', response.data.plans[0].planItemCollection);
      commit('PLAN_W_STACKING', response.data.plans[1].planItemCollection);
      */
      this.planWOStacking = response.data.plans[0];
      this.planWStacking = response.data.plans[1];
      return true;
    },
    async debtInit() {
      // pull debts
      const responseStatus1: boolean = await this.pullDebts();
      if (responseStatus1) {
        // calculate
        const responseStatus2 = await this.pushDebts(responseStatus1);
        if (responseStatus2) {
          // If true it will have Add a Debt open when going into the MyDebt Tab
          this.setAddADebtOpenFlag(false);
        }
      }
    },

    // export default {
    //   STEP: setStep,
    //   DEBTS: setDebts,
    //   PERSONINFO: setPersonInfo,
    //   DEBT: setDebt,
    //   DEBT_REMOVAL: deleteDebt,
    //   PLAN_WO_STACKING: setPlanWOStacking,
    //   PLAN_W_STACKING: setPlanWStacking,
    //   ADDADEBTOPEN: setAddADebtOpenFlag,
    //   SCENARIO: setScenario,
    //   resetState,
    // };
    // async callPushDebts(persondob: any) {
    //   await this.pushDebts(persondob);
    // },
    // setAddADebtOpen(openFlag) {
    //   commit('setAddADebtOpenFlag', openFlag);
    // },
    // setPersonInfo(personInfo) {
    //   commit('setPersonInfo', personInfo);
    // },
    // setScenario(scenarioObj) {
    //   commit('setScenario', scenarioObj);
    // },
    async createDebtItem(debtItem: IDebtSolutionsStoreDebt, persondob: string | null) {
      // setup ID
      let max = 0;
      let id = 0;
      const tDebts = this.debts;
      tDebts.forEach((element) => {
        id = Number.parseInt(element.debtID.toString(), 10) || 0;
        if (id > max) {
          max = id;
        }
      });
      const setNewId = (max || 0) + 1;
      debtItem.debtID = `${setNewId}`;
      // 30 max validation
      if (max !== 31) {
        // update DEBTS
        this.setDebt(debtItem);

        // sync to server and calculate
        await this.pushDebts(persondob);
        return true;
      }
      return false;
    },
    async updateDebtItem(debtItem: IDebtSolutionsStoreDebt, persondob: string | null) {
      // update DEBTS
      this.setDebt(debtItem);

      // sync to server and calculate
      await this.pushDebts(persondob);
      return true;
    },
    async deleteDebtItem(debtItem: IDebtSolutionsStoreDebt, persondob: string | null) {
      // update DEBTS
      this.deleteDebt(debtItem);

      // push to server and calculate
      await this.pushDebts(persondob);
      return true;
    },
  },
});
